import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import type { PageQuery } from '@/types/crud'

type YesNo = '' | 0 | 1

export interface CustomerStatisticsInternalFilters {
  corpId: string
  sid: string
  usedBy: string
  uname: string
  isNewRepeat: YesNo
  isDeletedWithin24Hours: YesNo
  isDeleted: YesNo
  isInAfterSales: YesNo
  hasCallOnAssignedDay: YesNo
  hasConnectedCallOnAssignedDay: YesNo
  isNoDevelopPendingTransfer: YesNo
  uid: string
  unionid: string
  thirdParty: string
  groupName: string
  sourceLabel: string
  createdAtRange: string[] | null
}

export interface CustomerStatisticsInternalRecord {
  avatar: string
  uname: string
  remark: string
  uid: string
  unionid: string
  corpName: string
  sid: string
  usedBy: string
  groupName: string
  sourceLabel: string
  thirdParty: string
  createdAt: string
  isNewRepeat: number
  isDeletedWithin24Hours: number
  isDeleted: number
  isInAfterSales: number
  hasCallOnAssignedDay: number
  hasConnectedCallOnAssignedDay: number
  isNoDevelopPendingTransfer: number
}

export function createParams(
  query: PageQuery<CustomerStatisticsInternalFilters>
) {
  const { createdAtRange, ...filters } = query.filters
  return {
    ...filters,
    createdAtBegin: createdAtRange?.[0],
    createdAtEnd: createdAtRange?.[1],
    pageNo: query.pageNo,
    pageSize: query.pageSize,
    orderBy: query.sort?.prop,
    orderType: query.sort?.order
  }
}

export async function fetchCustomerStatisticsInternalPage(
  query: PageQuery<CustomerStatisticsInternalFilters>,
  signal: AbortSignal
) {
  const payload = await requestV2.get<unknown>({
    url: '/main/customerStatistics/v2/queryByCondition',
    params: createParams(query),
    signal
  })
  return normalizePageResult<CustomerStatisticsInternalRecord>(payload)
}

export function exportCustomerStatisticsInternal(
  query: PageQuery<CustomerStatisticsInternalFilters>
) {
  return requestV2.get<Blob>({
    url: '/main/customerStatistics/v2/download',
    params: createParams(query),
    responseType: 'blob'
  })
}

export async function fetchSearchOptions() {
  const [companies, users, groups, channels, thirdParties] = await Promise.all([
    requestV2.get<{ id: string; name: string }[]>({
      url: '/main/enterpriseWechat/config/listAll'
    }),
    requestV2.get<{ userName: string }[]>({
      url: '/main/enterpriseWechat/dingUser/listAll'
    }),
    requestV2.get<string[]>({ url: '/main/userSucc/getGroupNameList' }),
    requestV2.get<string[]>({ url: '/main/customerChannel/queryAllChannel' }),
    requestV2.get<string[]>({ url: '/main/customerChannel/queryThirdParty' })
  ])
  const nameOptions = (names: string[]) =>
    names.map((name) => ({ label: name, value: name }))
  return {
    corpId: companies.map((item) => ({ label: item.name, value: item.id })),
    usedBy: nameOptions(users.map((item) => item.userName)),
    groupName: nameOptions(groups),
    sourceLabel: nameOptions(channels),
    thirdParty: nameOptions(thirdParties)
  }
}
